import {
  fetchNotifications,
  markOneRead,
  markAllRead,
  clearReadNotificationsApi,
  deleteNotificationApi,
} from "@/service/notification-api";
import { getSocket } from "@/service/socketio.service";

/**
 * Vuex Module: notifications
 * จัดการ State ของระบบแจ้งเตือนทั้งหมด รองรับ Real-time ผ่าน Socket.io
 */
const notificationModule = {
  namespaced: true,

  state: {
    notifications: [],   // รายการแจ้งเตือนทั้งหมด (max 30 ล่าสุด)
    unreadCount: 0,      // จำนวนที่ยังไม่อ่าน (ใช้แสดง Badge)
    loading: false,      // กำลังโหลดอยู่หรือเปล่า
    socketBound: false,  // ผูก Socket event แล้วหรือยัง (ป้องกัน duplicate)
  },

  getters: {
    notifications: (state) => state.notifications,
    unreadCount: (state) => state.unreadCount,
    loading: (state) => state.loading,
  },

  mutations: {
    SET_NOTIFICATIONS(state, notifications) {
      state.notifications = notifications;
    },
    SET_UNREAD_COUNT(state, count) {
      state.unreadCount = count;
    },
    PREPEND_NOTIFICATION(state, notification) {
      // เพิ่มรายการใหม่ไว้ด้านบนสุด (Real-time จาก Socket)
      state.notifications = [notification, ...state.notifications].slice(0, 30);
      state.unreadCount = Math.max(0, state.unreadCount + 1);
    },
    MARK_ONE_READ(state, id) {
      const notif = state.notifications.find((n) => n._id === id);
      if (notif && !notif.isRead) {
        notif.isRead = true;
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },
    MARK_ALL_READ(state) {
      state.notifications.forEach((n) => { n.isRead = true; });
      state.unreadCount = 0;
    },
    DELETE_NOTIFICATION(state, id) {
      const notif = state.notifications.find((n) => n._id === id);
      if (notif && !notif.isRead) {
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
      state.notifications = state.notifications.filter((n) => n._id !== id);
    },
    CLEAR_READ_NOTIFICATIONS(state) {
      state.notifications = state.notifications.filter((n) => !n.isRead);
    },
    SET_LOADING(state, val) {
      state.loading = val;
    },
    SET_SOCKET_BOUND(state, val) {
      state.socketBound = val;
    },
  },

  actions: {
    /**
     * โหลดรายการแจ้งเตือนจาก API
     */
    async loadNotifications({ commit }) {
      commit("SET_LOADING", true);
      try {
        const res = await fetchNotifications();
        commit("SET_NOTIFICATIONS", res.data.data || []);
        commit("SET_UNREAD_COUNT", res.data.unreadCount || 0);
      } catch (err) {
        console.error("[Notification] loadNotifications error:", err);
      } finally {
        commit("SET_LOADING", false);
      }
    },

    /**
     * ผูก Socket.io เพื่อรับแจ้งเตือน Real-time
     * มี Auto-retry หาก Socket ยังต่อไม่เสร็จในจังหวะแรก
     */
    bindSocket({ commit, state }) {
      if (state.socketBound) return;

      const tryBind = () => {
        const socket = getSocket();
        if (!socket || state.socketBound) return false;

        socket.off("new_notification");
        socket.off("notification_unread_count");

        // รับแจ้งเตือนใหม่ Real-time
        socket.on("new_notification", (notification) => {
          commit("PREPEND_NOTIFICATION", notification);
        });

        // อัปเดต Unread Badge จาก Server (หลัง mark-as-read)
        socket.on("notification_unread_count", ({ count }) => {
          commit("SET_UNREAD_COUNT", count);
        });

        commit("SET_SOCKET_BOUND", true);
        return true;
      };

      if (!tryBind()) {
        const interval = setInterval(() => {
          if (tryBind()) {
            clearInterval(interval);
          }
        }, 500);
        setTimeout(() => clearInterval(interval), 10000);
      }
    },

    /**
     * อ่านแจ้งเตือนทีละรายการ (Optimistic UI)
     */
    async markOneAsRead({ commit }, id) {
      commit("MARK_ONE_READ", id); // อัปเดต UI ทันที
      try {
        await markOneRead(id);
      } catch (err) {
        console.error("[Notification] markOneAsRead error:", err);
      }
    },

    /**
     * อ่านทั้งหมด (Optimistic UI)
     */
    async markAllAsRead({ commit }) {
      commit("MARK_ALL_READ"); // อัปเดต UI ทันที
      try {
        await markAllRead();
      } catch (err) {
        console.error("[Notification] markAllAsRead error:", err);
      }
    },

    /**
     * ล้างการแจ้งเตือนที่อ่านแล้วทั้งหมด (Optimistic UI)
     */
    async clearRead({ commit }) {
      commit("CLEAR_READ_NOTIFICATIONS");
      try {
        await clearReadNotificationsApi();
      } catch (err) {
        console.error("[Notification] clearRead error:", err);
      }
    },

    /**
     * ลบการแจ้งเตือนทีละรายการ (Optimistic UI)
     */
    async deleteOne({ commit }, id) {
      commit("DELETE_NOTIFICATION", id);
      try {
        await deleteNotificationApi(id);
      } catch (err) {
        console.error("[Notification] deleteOne error:", err);
      }
    },
  },
};

export default notificationModule;
